<template>
  <HeaderNavbar
    :title="$t('external_job_markets.title')"
    :breadcrumbs="[{ label: $t('nav.external_job_markets'), path: { name: 'external_job_markets.list' } }]" />

  <div class="container-lead">
    <!-- loading state for initial datapool fetch -->
    <div v-if="loading" class="mt-3">
      <div class="responsive-padding row gap-4">
        <div class="col-12">
          <div class="datapool-grid">
            <div v-for="i in 3" :key="i" class="card placeholder-glow">
              <div class="card-body">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <div class="placeholder col-5" style="height: 1.25rem" />
                  <div class="placeholder col-3 rounded-pill" style="height: 1.25rem" />
                </div>
                <div class="d-flex align-items-center justify-content-between">
                  <div class="placeholder col-4 rounded-pill" style="height: 1rem" />
                  <div class="placeholder col-5" style="height: 0.85rem" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- datapool overview cards -->
    <div v-else class="mt-3">
      <div class="responsive-padding row gap-4">
        <div class="col-12">
          <div class="datapool-grid">
            <div
              v-for="datapool in datapools"
              :key="datapool.id"
              class="card nav-card selectable-card"
              :class="{ selected: selectedDatapool?.id === datapool.id }"
              @click="selectDatapool(datapool)">
              <div class="card-body">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <h3 class="mb-0 text-nowrap fw-bold">{{ datapool.name }}</h3>
                  <span class="status" :class="datapool.active ? 'status-green' : 'status-red'">
                    <span class="status-dot" :class="datapool.active ? 'status-dot-animated' : ''"></span>
                    {{ datapool.active ? $t('common.active') : $t('common.inactive') }}
                  </span>
                </div>
                <div class="d-flex align-items-center justify-content-between">
                  <StatsPill :text="`${datapool.count_jobs} ${$t('external_job_markets.jobs_count')}`" />
                  <small class="text-muted">
                    {{ $t('external_job_markets.last_updated') }}:
                    {{ formatDateTime(datapool.last_build) }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- jobs list section -->
    <div v-if="selectedDatapool" class="my-4">
      <PageHeader
        :title="selectedDatapool.name"
        :subtitle="`${allJobs.length} ${$t('external_job_markets.jobs_count')}`"
        :has-filters="false">
        <div class="d-flex align-items-center gap-2">
          <GenericButton
            class="btn-white"
            :class="filtersOpen ? 'btn-dark' : 'btn-white'"
            :icon="IconFilter"
            icon-pos="start"
            @click="filtersOpen = !filtersOpen">
            {{ $t('external_job_markets.filters') }}
            <span
              v-if="ejm.activeFilterCount.value"
              class="badge ms-1"
              :class="filtersOpen ? 'bg-white text-primary' : 'bg-primary'">
              {{ ejm.activeFilterCount.value }}
            </span>
            <IconChevronDown :size="14" class="filter-chevron ms-1" :class="{ 'filter-chevron-open': filtersOpen }" />
          </GenericButton>
          <FormInput
            v-model="ejm.filters.value.search"
            size="sm"
            :icon="IconSearch"
            :placeholder="$t('external_job_markets.search_jobs')" />
        </div>
      </PageHeader>

      <!-- filter bar -->
      <div class="card my-3">
        <!-- top row: status toggles + count -->
        <div class="p-2">
          <div class="d-flex align-items-center flex-wrap gap-2">
            <div class="filters-list d-flex align-items-center flex-wrap gap-2">
              <ToggleButton
                :active="ejm.filters.value.status === ''"
                :text="$t('all')"
                @click="ejm.filters.value.status = ''" />
              <ToggleButton
                :active="ejm.filters.value.status === 'active'"
                :text="$t('common.active')"
                @click="ejm.filters.value.status = 'active'" />
              <ToggleButton
                :active="ejm.filters.value.status === 'expired'"
                :text="$t('external_job_markets.expired')"
                @click="ejm.filters.value.status = 'expired'" />

              <template v-if="mockApplicationForDemo">
                <div class="vr mx-1"></div>
                <ToggleButton
                  :active="ejm.filters.value.applicationStatus === 'no_applications'"
                  :text="$t('external_job_markets.no_applications')"
                  @click="
                    ejm.filters.value.applicationStatus =
                      ejm.filters.value.applicationStatus === 'no_applications' ? '' : 'no_applications'
                  " />
                <ToggleButton
                  :active="ejm.filters.value.hasHires === 'no'"
                  :text="$t('external_job_markets.no_hires')"
                  @click="ejm.filters.value.hasHires = ejm.filters.value.hasHires === 'no' ? '' : 'no'" />
                <ToggleButton
                  :active="ejm.filters.value.applicationStatus === 'below_target'"
                  :text="$t('external_job_markets.not_enough_applications')"
                  @click="
                    ejm.filters.value.applicationStatus =
                      ejm.filters.value.applicationStatus === 'below_target' ? '' : 'below_target'
                  " />
              </template>
            </div>

            <div class="ms-auto">
              <small class="text-muted">
                <strong>{{ ejm.filterCount.value }}</strong> / {{ allJobs.length }}
                {{ $t('external_job_markets.jobs_count') }}
              </small>
            </div>
          </div>
        </div>

        <!-- expandable filter panel -->
        <Transition name="filter-panel">
          <div v-if="filtersOpen" class="border-top">
            <div class="card-body py-3">
              <div class="filter-grid">
                <div class="filter-col">
                  <FilterButtonChecklist
                    :label="$t('external_job_markets.language')"
                    :modelValue="ejm.filters.value.languages"
                    @update:modelValue="ejm.filters.value.languages = $event"
                    :options="ejm.availableLanguages.value.map((l) => ({ label: l.toUpperCase(), value: l }))"
                    :reset-value="[]"
                    @reset="ejm.filters.value.languages = []" />
                </div>
                <div class="filter-col">
                  <FilterButtonSelect
                    :label="$t('external_job_markets.freshness')"
                    :modelValue="ejm.filters.value.freshness"
                    @update:modelValue="ejm.filters.value.freshness = $event"
                    :options="freshnessOptions"
                    :reset-value="''"
                    @reset="ejm.filters.value.freshness = ''" />
                </div>
                <div class="filter-col">
                  <FilterButtonAutocomplete
                    :label="$t('external_job_markets.location')"
                    :modelValue="ejm.filters.value.location || null"
                    @update:modelValue="ejm.filters.value.location = $event || ''"
                    :search="searchLocations"
                    :reset-value="null"
                    @reset="ejm.filters.value.location = ''" />
                </div>
                <div class="filter-col">
                  <FilterButtonAutocomplete
                    :label="$t('external_job_markets.company')"
                    :modelValue="ejm.filters.value.company || null"
                    @update:modelValue="ejm.filters.value.company = $event || ''"
                    :search="searchCompanies"
                    :reset-value="null"
                    @reset="ejm.filters.value.company = ''" />
                </div>

                <!-- application mock filters -->
                <template v-if="mockApplicationForDemo">
                  <div class="filter-col">
                    <FilterButtonSelect
                      :label="'Application Status'"
                      :modelValue="ejm.filters.value.applicationStatus"
                      @update:modelValue="ejm.filters.value.applicationStatus = $event"
                      :options="applicationStatusOptions"
                      :reset-value="''"
                      @reset="ejm.filters.value.applicationStatus = ''" />
                  </div>
                  <div class="filter-col">
                    <FilterButtonSelect
                      :label="'Hires'"
                      :modelValue="ejm.filters.value.hasHires"
                      @update:modelValue="ejm.filters.value.hasHires = $event"
                      :options="hasHiresOptions"
                      :reset-value="''"
                      @reset="ejm.filters.value.hasHires = ''" />
                  </div>
                </template>

                <!-- dynamic property filters -->
                <template v-for="prop in ejm.availableProperties.value" :key="prop.key">
                  <div class="filter-col">
                    <FilterButtonBool
                      v-if="prop.type === 'boolean'"
                      :label="prop.label"
                      :modelValue="getPropertyBoolValue(prop.key)"
                      @update:modelValue="setPropertyBoolValue(prop.key, $event)"
                      :reset-value="null"
                      @reset="ejm.clearPropertyFilter(prop.key)" />

                    <FilterButtonChecklist
                      v-else-if="prop.values.length <= 5"
                      :label="prop.label"
                      :modelValue="ejm.filters.value.properties[prop.key] || []"
                      @update:modelValue="ejm.filters.value.properties[prop.key] = $event"
                      :options="prop.values.map((v) => ({ label: v.label, value: v.value }))"
                      :reset-value="[]"
                      @reset="ejm.clearPropertyFilter(prop.key)" />

                    <FilterButtonMultiSelect
                      v-else
                      :label="prop.label"
                      :modelValue="ejm.filters.value.properties[prop.key] || []"
                      @update:modelValue="ejm.filters.value.properties[prop.key] = $event"
                      :options="prop.values.map((v) => ({ label: v.label, value: v.value }))" />
                  </div>
                </template>
              </div>
            </div>

            <!-- reset bar -->
            <div v-if="ejm.hasActiveFilters.value" class="card-footer py-2 d-flex justify-content-end">
              <button class="btn btn-sm btn-link text-danger p-0" @click="ejm.resetFilters()">
                {{ $t('external_job_markets.reset_filters') }}
              </button>
            </div>
          </div>
        </Transition>
      </div>

      <!-- loading jobs -->
      <div v-if="loadingJobs" class="card">
        <div class="card-body">
          <div class="d-flex flex-column align-items-center py-5">
            <div class="spinner-border text-primary mb-3" role="status"></div>
            <p class="text-muted mb-0">{{ $t('external_job_markets.loading_jobs') }}</p>
          </div>
        </div>
      </div>

      <!-- job list -->
      <div v-else-if="ejm.paginatedJobs.value.length > 0" class="card">
        <div class="card-body collection-wrapper">
          <EjmJobListElement
            v-for="job in ejm.paginatedJobs.value"
            :key="job.id"
            :job="job"
            :show-application-data="mockApplicationForDemo"
            @create-order="createOrderFromJob"
            class="animate__animated animate__fadeInUp animate__faster" />
        </div>

        <!-- pagination -->
        <div class="card-footer d-flex align-items-center justify-content-between">
          <small class="text-muted">
            {{ ejm.startRecordIndex.value }} - {{ ejm.endRecordIndex.value }} {{ $t('of') }}
            <strong>{{ ejm.filterCount.value }}</strong> {{ $t('entries') }}
          </small>
          <LiPagination
            :modelValue="ejm.currentPage.value"
            @update:modelValue="ejm.changePage"
            :page-count="ejm.pageCount.value"
            class="m-0"
            :labels="{ prev: $t('orders.pagination.prev'), next: $t('orders.pagination.next') }" />
        </div>
      </div>

      <!-- empty state -->
      <EmptyState
        v-else
        :icon="IconBriefcase"
        :title="$t('external_job_markets.no_jobs')"
        :subtitle="
          ejm.filters.value.search || ejm.hasActiveFilters.value
            ? $t('external_job_markets.no_jobs_search')
            : $t('external_job_markets.no_jobs_hint')
        ">
        <GenericButton v-if="ejm.hasActiveFilters.value" class="btn-white" :icon="IconX" @click="ejm.resetFilters()">
          {{ $t('external_job_markets.reset_filters') }}
        </GenericButton>
      </EmptyState>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { IconBriefcase, IconSearch, IconFilter, IconChevronDown, IconX } from '@tabler/icons-vue';
import {
  Pagination as LiPagination,
  FilterButtonSelect,
  FilterButtonChecklist,
  FilterButtonAutocomplete,
  FilterButtonBool,
  FormInput,
  PageHeader,
} from '@prospective/lithium';

import HeaderNavbar from '@/components/header/HeaderNavbar.vue';
import StatsPill from '@/components/generic/StatsPill.vue';
import ToggleButton from '@/components/common/ToggleButton.vue';
import EjmJobListElement from '@/components/externalJobMarket/EjmJobListElement.vue';
import FilterButtonMultiSelect from '@/components/generic/FilterButtonMultiSelect.vue';
import EmptyState from '@/components/common/EmptyState.vue';
import GenericButton from '@/components/generic/GenericButton.vue';

import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import { fetchExternalJobMarketDatapool, fetchAllExternalJobMarketJobs } from '@/api/externalJobMarket';
import { useEjmFilters } from '@/composables/useEjmFilters';
import type {
  ExternalJobMarketDatapool,
  ExternalJobMarketJob,
  FeatureExternalJobMarket,
} from '@/types/externalJobMarket';
import { formatDateTime } from '@/filters/dateFormat';
import translator from '@/locale/translator';
import { useAutopilotFunctions } from '@/composables/useAutopilotFunctions';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import { useAutopilotRouteReady } from '@/composables/useAutopilotRouteReady';
import { ejmWorkflowGuide } from '@/autopilot/functions/ejm/ejmWorkflow';
import type { AutopilotFunctionDefinition } from '@llmor/autopilot-core';

const mockApplicationForDemo = true;

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return s / 2147483647;
  };
}

function injectMockApplicationData(jobs: ExternalJobMarketJob[]) {
  for (const job of jobs) {
    const rng = seededRandom(job.id);
    const target = Math.floor(rng() * 46) + 5;
    const maxApps = Math.floor(target * 1.2);
    job.target_application_count = target;
    job.application_count = Math.floor(rng() * (maxApps + 1));
    job.hire_count = rng() < 0.25 ? Math.floor(rng() * 3) + 1 : 0;
  }
}

/* ---------------------------------- STATE --------------------------------- */
const router = useRouter();
const route = useRoute();
const featureStore = useCustomerFeaturesStore();
const loading = ref(true);
const loadingJobs = ref(false);
const filtersOpen = ref(false);
const datapools = ref<ExternalJobMarketDatapool[]>([]);
const selectedDatapool = ref<ExternalJobMarketDatapool | null>(null);
const allJobs = ref<ExternalJobMarketJob[]>([]);

const ejm = useEjmFilters(allJobs);
const routeReady = useAutopilotRouteReady();
const autopilotContextData = computed(() => ({
  name: 'ejm_list',
  description:
    'External Job Market list page. User can select datapools, browse jobs, and filter by search, status, language, location, company, freshness, application status, hires, and dynamic properties.',
  datapools: datapools.value.map((dp) => ({ id: dp.id, name: dp.name, active: dp.active, count_jobs: dp.count_jobs })),
  selectedDatapool: selectedDatapool.value
    ? {
        id: selectedDatapool.value.id,
        name: selectedDatapool.value.name,
        active: selectedDatapool.value.active,
        count_jobs: selectedDatapool.value.count_jobs,
      }
    : null,
  totalJobs: allJobs.value.length,
  filteredJobs: ejm.filterCount.value,
  currentPage: ejm.currentPage.value,
  pageCount: ejm.pageCount.value,
  visibleJobs: ejm.paginatedJobs.value.map((job) => ({
    id: job.id,
    title: job.title,
    company_name: job.company_name,
    language: job.language,
    location: job.workplace_location || job.workplace_city || null,
    active: job.active,
    provider: job.provider,
    expires: job.expires,
    created_at: job.created_at,
    application_count: job.application_count,
    target_application_count: job.target_application_count,
    hire_count: job.hire_count,
  })),
  activeFilters: {
    search: ejm.filters.value.search || undefined,
    status: ejm.filters.value.status || undefined,
    languages: ejm.filters.value.languages.length ? ejm.filters.value.languages : undefined,
    location: ejm.filters.value.location || undefined,
    company: ejm.filters.value.company || undefined,
    freshness: ejm.filters.value.freshness || undefined,
    applicationStatus: ejm.filters.value.applicationStatus || undefined,
    hasHires: ejm.filters.value.hasHires || undefined,
    properties: Object.values(ejm.filters.value.properties).some((v) => v.length > 0)
      ? ejm.filters.value.properties
      : undefined,
  },
  availableLanguages: ejm.availableLanguages.value,
  availableProperties: ejm.availableProperties.value.map((p) => ({
    key: p.key,
    label: p.label,
    type: p.type,
    values: p.values.map((v) => v.value),
  })),
  loading: loadingJobs.value,
}));

/* -------------------------------- COMPUTED -------------------------------- */
const externalJobMarketFeature = computed(() => {
  return featureStore.features.external_job_market as FeatureExternalJobMarket | undefined;
});

const freshnessOptions = [
  { label: translator.get('all'), value: '' },
  { label: translator.get('external_job_markets.freshness.today'), value: 'today' },
  { label: translator.get('external_job_markets.freshness.7days'), value: '7days' },
  { label: translator.get('external_job_markets.freshness.30days'), value: '30days' },
];

const applicationStatusOptions = [
  { label: translator.get('all'), value: '' },
  { label: 'Below Target', value: 'below_target' },
  { label: 'On Target', value: 'on_target' },
  { label: 'Above Target', value: 'above_target' },
];

const hasHiresOptions = [
  { label: translator.get('all'), value: '' },
  { label: 'Has Hires', value: 'yes' },
  { label: 'No Hires', value: 'no' },
];

/* -------------------------------- METHODS --------------------------------- */
const searchJobField = (
  query: string,
  extractor: (job: ExternalJobMarketJob) => string | null | undefined
): string[] => {
  const q = query.toLowerCase();
  const values = new Set<string>();
  for (const job of allJobs.value) {
    const val = extractor(job);
    if (val && val.toLowerCase().includes(q)) values.add(val);
  }
  return Array.from(values).sort().slice(0, 20);
};

const searchLocations = async (query: string): Promise<string[]> => {
  return searchJobField(query, (job) => job.workplace_location || job.workplace_city);
};

const searchCompanies = async (query: string): Promise<string[]> => {
  return searchJobField(query, (job) => job.company_name);
};

const getPropertyBoolValue = (key: string): boolean | null => {
  const vals = ejm.filters.value.properties[key] || [];
  if (vals.length === 0) return null;
  return vals.includes('true');
};

const setPropertyBoolValue = (key: string, value: boolean | null) => {
  if (value === null) {
    ejm.clearPropertyFilter(key);
  } else {
    ejm.filters.value.properties[key] = [String(value)];
  }
};

const selectDatapool = async (datapool: ExternalJobMarketDatapool) => {
  selectedDatapool.value = datapool;
  ejm.resetFilters();
  router.replace({ name: 'external_job_markets.list', params: { datapoolId: datapool.id } });
  await loadJobs(datapool.id);
};

const loadJobs = async (datapoolId: number) => {
  loadingJobs.value = true;
  try {
    const response = await fetchAllExternalJobMarketJobs(datapoolId);
    if (mockApplicationForDemo) injectMockApplicationData(response.data);
    allJobs.value = response.data;
  } catch (error) {
    console.error('Error fetching jobs:', error);
    allJobs.value = [];
  } finally {
    loadingJobs.value = false;
  }
};

const createOrderFromJob = (job: ExternalJobMarketJob) => {
  // Store the job data in session storage for the order creation page
  sessionStorage.setItem('ejm_selected_job', JSON.stringify(job));

  // Navigate to order creation page with query parameters
  router.push({
    name: 'order.create',
    query: {
      ejm_job_id: job.id.toString(),
      url: job.link,
    },
  });
};

/* ---------------------------- LIFECYCLE HOOKS ----------------------------- */
onMounted(async () => {
  try {
    await featureStore.fetchFeatures();

    const feature = externalJobMarketFeature.value;
    if (!feature || !feature.active_datapool_ids.length) {
      console.warn('No active datapool IDs found in external job market feature');
      loading.value = false;
      return;
    }

    const datapoolPromises = feature.active_datapool_ids.map((id) => fetchExternalJobMarketDatapool(id));
    const responses = await Promise.all(datapoolPromises);
    datapools.value = responses.map((response) => response.data);

    const datapoolId = route.params.datapoolId as string | undefined;
    const match = datapoolId ? datapools.value.find((dp) => String(dp.id) === datapoolId) : null;

    const selected = match || datapools.value[0];
    if (selected) {
      selectedDatapool.value = selected;
      if (!match) {
        router.replace({ name: 'external_job_markets.list', params: { datapoolId: selected.id } });
      }
      await loadJobs(selected.id);
    }
  } catch (error) {
    console.error('Error fetching external job market data:', error);
  } finally {
    loading.value = false;
  }

  routeReady();
});

/* -------------------------------- AUTOPILOT ------------------------------- */
useAutopilotContext('ejm_workflow', () => ejmWorkflowGuide);
useAutopilotContext('current_page', () => ({ ...autopilotContextData.value }));

const searchAndFilterEjmJobs: AutopilotFunctionDefinition = {
  name: 'search_and_filter_ejm_jobs',
  description:
    'Search and filter jobs on the External Job Market page. Pass only the parameters you want to change; omitted parameters remain unchanged. Pass reset=true to clear all filters first. IMPORTANT: Prefer using specific filters over free-text search. Check "availableProperties" in context for dynamic property filters and their possible values, and use the "properties" parameter to filter by them when the user\'s request matches a property or value. Only use "search" as a fallback when no specific filter or property matches the user\'s intent.',
  parameters: {
    type: 'object',
    properties: {
      reset: {
        type: 'boolean',
        description: 'If true, reset all filters to defaults before applying new ones',
      },
      search: {
        type: 'string',
        description:
          'Free text search across job title, company, location, city, and provider. Set to empty string to clear.',
      },
      status: {
        type: 'string',
        enum: ['', 'active', 'expired'],
        description: 'Filter by job status: empty for all, "active" for active jobs, "expired" for expired jobs',
      },
      languages: {
        type: 'array',
        items: { type: 'string' },
        description: 'Filter by language codes (e.g. ["de", "fr"]). Check context for available languages.',
      },
      location: {
        type: 'string',
        description: 'Filter by workplace location (partial match on location or city)',
      },
      company: {
        type: 'string',
        description: 'Filter by company name (partial match)',
      },
      freshness: {
        type: 'string',
        enum: ['', 'today', '7days', '30days'],
        description: 'Filter by job freshness/age',
      },
      applicationStatus: {
        type: 'string',
        enum: ['', 'no_applications', 'below_target', 'on_target', 'above_target'],
        description: 'Filter by application status relative to target',
      },
      hasHires: {
        type: 'string',
        enum: ['', 'yes', 'no'],
        description: 'Filter by whether jobs have hires',
      },
      properties: {
        type: 'object',
        additionalProperties: {
          type: 'array',
          items: { type: 'string' },
        },
        description:
          'Dynamic property filters. Keys are property names, values are arrays of selected values. Check context for available properties.',
      },
      page: {
        type: 'number',
        description: 'Navigate to a specific page number (1-based)',
      },
    },
    required: [],
  },
  handler: async (rawArgs) => {
    const args = (rawArgs ?? {}) as {
      reset?: boolean;
      search?: string;
      status?: '' | 'active' | 'expired';
      languages?: string[];
      location?: string;
      company?: string;
      freshness?: '' | 'today' | '7days' | '30days';
      applicationStatus?: '' | 'no_applications' | 'below_target' | 'on_target' | 'above_target';
      hasHires?: '' | 'yes' | 'no';
      properties?: Record<string, string[]>;
      page?: number;
    };
    if (!selectedDatapool.value) {
      return { success: false, message: 'No datapool selected. Use switch_ejm_datapool first.' };
    }

    if (args.reset) {
      ejm.resetFilters();
    }

    if (args.search !== undefined) ejm.filters.value.search = args.search;
    if (args.status !== undefined) ejm.filters.value.status = args.status;
    if (args.languages !== undefined) ejm.filters.value.languages = args.languages;
    if (args.location !== undefined) ejm.filters.value.location = args.location;
    if (args.company !== undefined) ejm.filters.value.company = args.company;
    if (args.freshness !== undefined) ejm.filters.value.freshness = args.freshness;
    if (args.applicationStatus !== undefined) ejm.filters.value.applicationStatus = args.applicationStatus;
    if (args.hasHires !== undefined) ejm.filters.value.hasHires = args.hasHires;
    if (args.properties !== undefined) {
      for (const [key, values] of Object.entries(args.properties)) {
        ejm.filters.value.properties[key] = values;
      }
    }
    if (args.page !== undefined) ejm.changePage(args.page);

    if (ejm.hasActiveFilters.value) {
      filtersOpen.value = true;
    }

    return {
      success: true,
      appliedFilters: { ...ejm.filters.value },
      totalJobs: allJobs.value.length,
      filteredJobs: ejm.filterCount.value,
      currentPage: ejm.currentPage.value,
      pageCount: ejm.pageCount.value,
      message: `Filters applied. Showing ${ejm.filterCount.value} of ${allJobs.value.length} jobs.`,
    };
  },
};

const switchEjmDatapool: AutopilotFunctionDefinition = {
  name: 'switch_ejm_datapool',
  description:
    'Switch to a different External Job Market datapool. This loads all jobs from the selected datapool and resets filters.',
  parameters: {
    type: 'object',
    properties: {
      datapool_id: {
        type: 'number',
        description: 'The ID of the datapool to switch to. Check context for available datapools.',
      },
    },
    required: ['datapool_id'],
  },
  handler: async (rawArgs) => {
    const args = (rawArgs ?? {}) as { datapool_id: number };
    const target = datapools.value.find((dp) => dp.id === args.datapool_id);
    if (!target) {
      return {
        success: false,
        message: `Datapool with ID ${args.datapool_id} not found. Available datapools: ${datapools.value.map((dp) => `${dp.name} (ID: ${dp.id})`).join(', ')}`,
      };
    }

    await selectDatapool(target);

    return {
      success: true,
      datapool: { id: target.id, name: target.name, active: target.active, count_jobs: target.count_jobs },
      totalJobs: allJobs.value.length,
      message: `Switched to datapool "${target.name}" with ${allJobs.value.length} jobs.`,
    };
  },
};

const getEjmJobDetails: AutopilotFunctionDefinition = {
  name: 'get_ejm_job_details',
  description: 'Get detailed information about a specific job from the External Job Market by its ID.',
  parameters: {
    type: 'object',
    properties: {
      job_id: {
        type: 'number',
        description: 'The ID of the job to get details for. Use IDs from the visible jobs in the context.',
      },
    },
    required: ['job_id'],
  },
  handler: async (rawArgs) => {
    const args = (rawArgs ?? {}) as { job_id: number };
    const job = allJobs.value.find((j) => j.id === args.job_id);
    if (!job) {
      return { success: false, message: `Job with ID ${args.job_id} not found in the current datapool.` };
    }

    const jobDetails = { ...job };
    delete (jobDetails as any).crawler_id;
    delete (jobDetails as any).last_execution_id;
    return { success: true, job: jobDetails };
  },
};

useAutopilotFunctions([searchAndFilterEjmJobs, switchEjmDatapool, getEjmJobDetails]);
</script>

<style scoped lang="scss">
.datapool-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.collection-wrapper > :deep(div:not(:first-of-type)) {
  border-top: 1px solid var(--tblr-border-color);
}

.filters-list .btn {
  font-size: 13px;
  padding: 0.25rem 0.75rem;
}

.filter-chevron {
  transition: transform 0.2s ease;
}

.filter-chevron-open {
  transform: rotate(180deg);
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.35rem;
}

.filter-panel-enter-active,
.filter-panel-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.filter-panel-enter-from,
.filter-panel-leave-to {
  opacity: 0;
  max-height: 0;
}

.filter-panel-enter-to,
.filter-panel-leave-from {
  opacity: 1;
  max-height: 800px;
}
</style>
